import React, { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import Map from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { debounce } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setMapBounds, setZoomLevel } from '../store/reducers/map';
import { DOES_NOT_SUPPORT_MAPBOX } from '../constants/strings';

const accessToken = import.meta.env.VITE_MAPBOX_API_KEY;

const styles = {
  position: 'relative',
  height: '100%',
  width: '100%',
};

const MAP_STYLE = 'mapbox://styles/mapbox/streets-v11?optimize=true';

const getZoomValue = (zoom) => (Array.isArray(zoom) ? zoom[0] : zoom);

// "unsupported browser" fallback behaviour.
const isWebglSupported = () => {
  if (typeof window === 'undefined' || !window.WebGL2RenderingContext) {
    return false;
  }
  try {
    return !!document.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
};

const Mapbox = ({ children, handleOnLoad, coordinates, zoom, ...rest }) => {
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const isFirstUpdate = useRef(true);

  const isSupported = useMemo(() => isWebglSupported(), []);

  const changeZoom = useCallback(
    (zoom) => {
      dispatch(setZoomLevel(zoom));
    },
    [dispatch]
  );

  const debouncedZoomHandler = useMemo(
    () => debounce(changeZoom, 300),
    [changeZoom]
  );

  const onMapMoved = useCallback(
    (map) => {
      const bounds = map?.getBounds().toArray().flat();
      dispatch(setMapBounds(bounds));
    },
    [dispatch]
  );

  const debouncedMoveHandler = useMemo(
    () => debounce(onMapMoved, 300),
    [onMapMoved]
  );

  useEffect(() => {
    if (isFirstUpdate.current) {
      isFirstUpdate.current = false;
      return;
    }
    const map = mapRef.current;
    if (!map || !coordinates) {
      return;
    }
    map.flyTo({ center: coordinates, zoom: getZoomValue(zoom) });
  }, [coordinates, zoom]);

  return isSupported ? (
    <Map
      ref={mapRef}
      mapboxAccessToken={accessToken}
      mapStyle={MAP_STYLE}
      initialViewState={{
        longitude: coordinates?.[0],
        latitude: coordinates?.[1],
        zoom: getZoomValue(zoom),
      }}
      style={styles}
      onLoad={(event) => handleOnLoad && handleOnLoad(event.target)}
      onZoom={(event) => {
        debouncedZoomHandler([event.target.getZoom()]);
      }}
      onMoveEnd={(event) => {
        debouncedMoveHandler(event.target);
      }}
      {...rest}
    >
      {children}
    </Map>
  ) : (
    <p>{DOES_NOT_SUPPORT_MAPBOX}</p>
  );
};

export default memo(Mapbox);
